import React, { useState, memo, useMemo, useEffect, useCallback } from "react";
import { Spin, Modal, Button, message } from "antd";
import StickyBox from "react-sticky-box";
import { hidePanel as hidePanelAction } from "@/pages/shared/shared.reducer";
import { useDispatch, useSelector } from "react-redux";
import get from "lodash.get";
import { LEAVE_PAGE } from "@/constants/constant.string";
import { useOptionalFormContext } from "@/common/form-context";

const Panel = memo(
  function Panel({
    id,
    backText,
    title,
    subtitle,
    content,
    zIndex,
    className,
    exitPanel,
  }) {
    const dispatch = useDispatch();
    const visible = useSelector(state => get(state.shared, `${id}.visible`));
    const loading = useSelector(state => get(state.shared, `${id}.loading`));

    const hidePanel = useCallback((panelId) => dispatch(hidePanelAction(panelId)), [dispatch]);

    const formContext = useOptionalFormContext();

  const { form, isAnyFieldTouched, clearTouchedFields, customCancelMessage, submitFormAsync } =
      formContext || {};

    const [localVisible, setLocalVisible] = useState(visible);
    const [formLoading, setFormLoading] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
      setLocalVisible(visible);
    }, [visible]);

    const onModalClose = useCallback(() => {
      if (formContext && isAnyFieldTouched && isAnyFieldTouched()) {
        setOpen(true);
        return;
      } else {
        hidePanel(id);
        if (form) {
          form.resetFields();
        }
        if (exitPanel) {
          exitPanel();
        }
      }
    }, [formContext, isAnyFieldTouched, hidePanel, id, form, exitPanel]);

    const modalClose = () => {
      if (!formContext) {
        return null;
      }

      const handleOk = () => {
        if (clearTouchedFields) {
          clearTouchedFields();
        }
        setOpen(false);
        setLocalVisible(false);
        hidePanel(id);
        if (form) {
          form.resetFields();
        }
        if (exitPanel) {
          exitPanel();
        }
        setFormLoading(false);
      };
      
      const handleCancel = () => {
        setOpen(false);
      };
      
      return (
        <Modal
          title={
            <div>
              <i className="ri-error-warning-line me-2" />
              {LEAVE_PAGE}
            </div>
          }
          className="confirm-cancellation"
          open={open}
          onOk={handleOk}
          onCancel={handleCancel}
          footer={
            <div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
              <Button key="back" onClick={() => handleCancel()}>
                Keep Editing
              </Button>
              <Button danger onClick={() => handleOk()} >
                <i className="ri-delete-bin-4-line"></i>
                Discard Changes
              </Button>
              <Button
                loading={formLoading}
                type="primary"
                onClick={async () => {
                  setFormLoading(true);
                  try {
                    if (submitFormAsync) {
                      await submitFormAsync();
                    } else if (form) {
                      await form.validateFields();
                      form.submit();
                    }
                    handleOk();
                  } catch (error) {
                    setFormLoading(false);
                    setOpen(false);
                    if (error?.errorFields?.length) {
                      message.warning(
                        "Some required details are missing or invalid. Please review the highlighted fields."
                      );
                    }
                    return;
                  }
                }}
              >
                <i className="ri-save-line"></i>
                Save and Close
              </Button>
            </div>
          }
        >
          {customCancelMessage ? (
            customCancelMessage
          ) : (
            <p>Are you sure you wish to close without saving?</p>
          )}
        </Modal>
      );
    };

    const getTitle = useMemo(() => {
      return (
        <div className="row title-for-modal width_100_per">
          {backText && (
            <div className="col-12 mb-3">
              <Button
                type="link"
                className="display-center ps-0"
                onClick={onModalClose}
              >
                <i className="ri-arrow-left-s-line me-2" />
                {backText}
              </Button>
            </div>
          )}
          <div className="no-padding-left">
            <div className="title-wrapper">
              <div className="display-center">
                <h3 className="me-2 heading-01" style={{ fontWeight: 700 }}>
                  {title}
                </h3>
                <h4>{subtitle} </h4>
              </div>
              <Button className="common-button" onClick={onModalClose}>
                <i className="ri-close-line"></i>
                Close
              </Button>
            </div>
          </div>
        </div>
      );
  }, [backText, title, subtitle, onModalClose]);

    return visible ? (
      <>
        <Modal
          zIndex={zIndex}
          footer={null}
          className={`document-detail-modal ${className}`}
          closable={false}
          open={localVisible} 
          onCancel={onModalClose}
          mask={{ closable: true }}
        >
          <StickyBox offsetTop={-20} offsetBottom={0}
            style={{
              zIndex: 20,
            }}>
            <div className="panel-sticky-header">{getTitle}</div>
          </StickyBox>
          {loading ? (
            <div style={{ alignItems: "center", display: "flex", justifyContent: "center", height: "100%" }} className="loading-wrapper">
              <Spin size="large" />
            </div>
          ) : (
            React.cloneElement(content, { exitPanel: onModalClose })
          )}
        </Modal>
        {modalClose()}
      </>
    ) : null;
  },
  (prevProps, nextProps) => {
    if (
      prevProps.id === nextProps.id &&
      prevProps.title === nextProps.title &&
      prevProps.subtitle === nextProps.subtitle &&
      prevProps.className === nextProps.className &&
      prevProps.zIndex === nextProps.zIndex &&
      prevProps.backText === nextProps.backText &&
      prevProps.content === nextProps.content
    ) {
      return true;
    }
    return false;
  }
);

Panel.displayName = "Panel";

export default Panel;
